import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(null);

  // Auto-logout after 10 minutes of inactivity
  const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
  const WARNING_TIMEOUT = 9 * 60 * 1000; // Show warning at 9 minutes (1 min before logout)
  const [inactivityTimer, setInactivityTimer] = useState(null);
  const [warningTimer, setWarningTimer] = useState(null);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);

  // Tax residency and location tracking
  const [showTaxResidencyModal, setShowTaxResidencyModal] = useState(false);
  const [showLocationChangeModal, setShowLocationChangeModal] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [locationCheckData, setLocationCheckData] = useState(null);
  const [taxResidencyConfirmed, setTaxResidencyConfirmed] = useState(false); // Local flag to prevent modal reopening

  // Initialize auth token from localStorage
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      setAuthToken(token);
      checkAuth(token);
    } else {
      setLoading(false);
    }
  }, []);

  // Setup inactivity tracking when user is authenticated
  useEffect(() => {
    if (!user) {
      // Clear timer if user logs out
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
        setInactivityTimer(null);
      }
      return;
    }

    // Function to reset inactivity timer
    const resetInactivityTimer = () => {
      // Clear existing timers
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      if (warningTimer) {
        clearTimeout(warningTimer);
      }

      // Hide warning if shown
      setShowInactivityWarning(false);

      // Set warning timer (1 minute before logout)
      const warnTimer = setTimeout(() => {
        console.log('User will be logged out in 1 minute due to inactivity');
        setShowInactivityWarning(true);
      }, WARNING_TIMEOUT);
      setWarningTimer(warnTimer);

      // Set logout timer (10 minutes)
      const logoutTimer = setTimeout(() => {
        console.log('User inactive for 10 minutes - logging out');
        setShowInactivityWarning(false);
        logout();
      }, INACTIVITY_TIMEOUT);
      setInactivityTimer(logoutTimer);
    };

    // Activity events to track
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, resetInactivityTimer);
    });

    // Initialize timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      if (warningTimer) {
        clearTimeout(warningTimer);
      }
      events.forEach(event => {
        document.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, [user]); // Re-run when user changes

  // Function to extend session (user acknowledges warning)
  const extendSession = () => {
    setShowInactivityWarning(false);
    // This will trigger a reset of all timers through the activity
    document.dispatchEvent(new Event('mousedown'));
  };

  const checkAuth = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = response.data;
      setUser(userData);

      // Check if user needs to confirm tax residency (first time)
      // Use local flag to prevent reopening after confirmation
      if (!userData.tax_residency_confirmed && !taxResidencyConfirmed) {
        setDetectedCountry(userData.country);
        setShowTaxResidencyModal(true);
      } else {
        // Check for location changes (returning users)
        checkLocationChange(token);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('auth_token');
      setAuthToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const checkLocationChange = async (token) => {
    try {
      const response = await axios.get(`${API}/country/check-location-change`, {
        headers: { Authorization: `Bearer ${token || authToken}` }
      });
      const data = response.data;
      
      if (data.location_changed) {
        setLocationCheckData(data);
        setShowLocationChangeModal(true);
      }
    } catch (error) {
      console.error('Location check failed:', error);
    }
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name
    });
    const token = response.data.access_token;
    localStorage.setItem('auth_token', token);
    setAuthToken(token);
    await checkAuth(token);
    return response.data;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, {
      email,
      password
    });
    const token = response.data.access_token;
    localStorage.setItem('auth_token', token);
    setAuthToken(token);
    await checkAuth(token);
    return response.data;
  };

  const loginWithGoogle = () => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async () => {
    // Get session_id from URL fragment
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get('session_id');

    console.log('Google callback - hash:', hash);
    console.log('Google callback - sessionId:', sessionId);

    if (sessionId) {
      try {
        console.log('Exchanging session_id for user data...');
        // Exchange session_id for user data
        const response = await axios.post(`${API}/auth/google/session`, {}, {
          headers: { 'X-Session-ID': sessionId }
        });
        
        console.log('Google auth response:', response.data);
        
        const token = response.data.access_token;
        if (!token) {
          console.error('No access_token in response');
          console.error('Full response:', JSON.stringify(response.data));
          return false;
        }
        
        // Store token and user data
        localStorage.setItem('auth_token', token);
        setAuthToken(token);
        setUser(response.data.user);
        
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return true;
      } catch (error) {
        console.error('Google auth failed:', error);
        console.error('Error response:', error.response?.data);
        return false;
      }
    }
    console.error('No session_id found in URL');
    return false;
  };

  const logout = async () => {
    try {
      if (authToken) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      setAuthToken(null);
      setUser(null);
    }
  };

  // Handle tax residency confirmation
  const handleTaxResidencyConfirm = (country) => {
    console.log('[AuthContext] handleTaxResidencyConfirm called with country:', country);
    
    // Set local flag to prevent modal from reopening
    setTaxResidencyConfirmed(true);
    
    // Close modal immediately
    setShowTaxResidencyModal(false);
    console.log('[AuthContext] Modal closed');
    
    // Update user state
    setUser(prev => ({
      ...prev,
      country,
      tax_residency_confirmed: true
    }));
    
    // Refresh user data from backend asynchronously
    setTimeout(async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          console.log('[AuthContext] User data refreshed from backend');
        }
      } catch (error) {
        console.error('[AuthContext] Failed to refresh user data:', error);
      }
    }, 1000);
  };

  // Handle location change update
  const handleLocationUpdate = (country) => {
    setShowLocationChangeModal(false);
    // Update user state
    setUser(prev => ({
      ...prev,
      country
    }));
  };

  // Handle location change dismiss
  const handleLocationChangeDismiss = () => {
    setShowLocationChangeModal(false);
  };

  const value = {
    user,
    authToken,
    loading,
    register,
    login,
    loginWithGoogle,
    handleGoogleCallback,
    logout,
    isAuthenticated: !!user,
    showInactivityWarning,
    extendSession,
    showTaxResidencyModal,
    showLocationChangeModal,
    detectedCountry,
    locationCheckData,
    handleTaxResidencyConfirm,
    handleLocationUpdate,
    handleLocationChangeDismiss,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
